import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { translateApiError } from '../i18n/errors';
import { useApp } from '../store';
import { MobileSheetHandle, useMobileSheetExpand } from './MobileSheet';

export function AuthPanel() {
  const { t } = useTranslation(['panels', 'common']);
  const { t: tError } = useTranslation('errors');
  const { setUser, setWallet, setZones, setPanel } = useApp();
  const { className: sheetExpandClassName } = useMobileSheetExpand('sm');
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res =
        tab === 'login'
          ? await api.login(username, password)
          : await api.register(username, email, password);
      setUser(res.user);
      setWallet(res.wallet);
      if ('zones' in res) setZones((res as { zones?: import('../lib/api').ZonesStatus | null }).zones ?? null);
      setPanel(res.user.needsEmpireName ? 'profile' : 'none');
    } catch (err) {
      setError(translateApiError(err, tError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className={`absolute inset-x-0 bottom-0 z-30 max-h-[85vh] overflow-auto rounded-t-3xl border border-[var(--line)] bg-[var(--bg-panel)] p-5 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-auto sm:w-[22rem] sm:rounded-3xl ${sheetExpandClassName}`}>
      <MobileSheetHandle />
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-[family-name:var(--font-display)] text-xl text-[var(--accent)]">
            {tab === 'login' ? t('auth.login') : t('auth.createAccount')}
          </h2>
        </div>
        <button data-panel-close type="button" className="text-[var(--muted)]" onClick={() => setPanel('none')}>
          {t('common:close')}
        </button>
      </div>

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          className={`flex-1 rounded-xl px-3 py-2 text-sm ${tab === 'login' ? 'bg-white/10' : 'text-[var(--muted)]'}`}
          onClick={() => setTab('login')}
        >
          {t('auth.login')}
        </button>
        <button
          type="button"
          className={`flex-1 rounded-xl px-3 py-2 text-sm ${tab === 'register' ? 'bg-white/10' : 'text-[var(--muted)]'}`}
          onClick={() => setTab('register')}
        >
          {t('auth.register')}
        </button>
      </div>

      <form className="flex flex-col gap-3" onSubmit={submit}>
        <label className="text-xs text-[var(--muted)]">
          {t('auth.username')}
          <input
            className="mt-1 w-full rounded-xl border border-[var(--line)] bg-[#0b1219] px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        {tab === 'register' && (
          <label className="text-xs text-[var(--muted)]">
            {t('auth.email')}
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-[var(--line)] bg-[#0b1219] px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
        )}
        <label className="text-xs text-[var(--muted)]">
          {t('auth.password')}
          <input
            type="password"
            className="mt-1 w-full rounded-xl border border-[var(--line)] bg-[#0b1219] px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
            required
          />
        </label>
        {error && <p className="text-sm text-[var(--danger)]">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[#1a1408] disabled:opacity-60"
        >
          {busy
            ? t('common:loadingShort')
            : tab === 'login'
              ? t('auth.submitLogin')
              : t('auth.submitRegister')}
        </button>
      </form>
    </aside>
  );
}
